import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Activity, ArrowLeft, Mail } from 'lucide-react';

const ForgotPasswordFirebase = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Veuillez entrer votre adresse email');
      return;
    }

    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSent(true);
      toast.success('✅ Email de réinitialisation envoyé !');
    } catch (error) {
      console.error('Error sending reset email:', error);
      
      let message = 'Erreur lors de l\'envoi de l\'email';
      if (error.code === 'auth/user-not-found') {
        message = 'Aucun compte trouvé avec cet email';
      } else if (error.code === 'auth/invalid-email') {
        message = 'Email invalide';
      } else if (error.code === 'auth/too-many-requests') {
        message = 'Trop de tentatives. Réessayez plus tard';
      }
      
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center space-x-2">
              <Activity className="w-8 h-8 text-blue-600" />
              <CardTitle className="text-2xl font-bold">Omevita</CardTitle>
            </div>
            <CardDescription>Réinitialisation du mot de passe</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-800 text-sm">
                ✅ <strong>Email envoyé !</strong>
              </p>
              <p className="text-green-700 text-sm mt-2">
                Un email de réinitialisation a été envoyé à <strong>{email}</strong>.
              </p>
              <p className="text-green-700 text-sm mt-2">
                Veuillez vérifier votre boîte de réception (et vos spams).
              </p>
            </div>

            <Link to="/login">
              <Button variant="outline" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </Link>

            <div className="text-center text-sm text-gray-600">
              Vous n'avez pas reçu l'email ?
              <button
                onClick={() => {
                  setSent(false);
                  toast.info('Vous pouvez réessayer');
                }}
                className="ml-1 text-blue-600 hover:underline"
              >
                Réessayer
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    }}>
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center space-x-2">
            <Activity className="w-8 h-8 text-blue-600" />
            <CardTitle className="text-2xl font-bold">Omevita</CardTitle>
          </div>
          <CardDescription>Réinitialisation du mot de passe</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Adresse email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  placeholder="votre@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                />
              </div>
              <p className="text-xs text-gray-600">
                Entrez votre adresse email. Firebase vous enverra un lien pour réinitialiser votre mot de passe.
              </p>
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white'
              }}
            >
              {loading ? 'Envoi en cours...' : 'Envoyer l\'email de réinitialisation'}
            </Button>

            <Link to="/login">
              <Button variant="outline" className="w-full" type="button">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Retour à la connexion
              </Button>
            </Link>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            <p className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              🔥 <strong>Firebase Auth:</strong> L'email sera envoyé automatiquement par Firebase.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForgotPasswordFirebase;
